import { Capacitor, registerPlugin } from "@capacitor/core";
import { t } from "@lingui/core/macro";
import { sentryLog } from "@/utils/sentry-utils";
import { DevicePrinterUserError } from "@/utils/pos-printer/printer-errors";

export type SunmiPrinterInfo = {
  available: boolean;
  model?: string;
  serialNumber?: string;
};

type SunmiPrinterPlugin = {
  getPrinterInfo(): Promise<SunmiPrinterInfo>;
  printEscPos(options: { data: string }): Promise<void>;
};

const SunmiPrinter = registerPlugin<SunmiPrinterPlugin>("SunmiPrinter");

/** Chunked so a long receipt cannot blow the argument limit of `apply`. */
export function encodeBase64(bytes: Uint8Array): string {
  const CHUNK_SIZE = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK_SIZE) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + CHUNK_SIZE),
    );
  }
  return btoa(binary);
}

/**
 * Whether this device has Sunmi's built-in thermal printer. False on every
 * non-Sunmi device, and on iOS, where the vendor service does not exist.
 */
export async function getSunmiPrinterInfo(): Promise<SunmiPrinterInfo> {
  if (Capacitor.getPlatform() !== "android") {
    return { available: false };
  }

  try {
    return await SunmiPrinter.getPrinterInfo();
  } catch (error) {
    // An app shell built before the plugin existed. Not a Sunmi device as far
    // as this build is concerned, but worth knowing about if it spikes.
    sentryLog.warn("Sunmi printer probe failed", {
      cause: error instanceof Error ? error.message : String(error),
    });
    return { available: false };
  }
}

/**
 * Sunmi's printer settings carry "forced" styles (`强制`) — forced double
 * width/height and forced anti-white — which override the print data instead
 * of being overridden by it. With those on, receipts come out oversized and
 * white-on-black no matter what the stream asks for, including ESC @. It is a
 * device setting, not something this layer can fix.
 */
export async function printEscPosOnSunmi(bytes: Uint8Array): Promise<void> {
  try {
    await SunmiPrinter.printEscPos({ data: encodeBase64(bytes) });
  } catch (error) {
    throw new DevicePrinterUserError(
      t`Tiskanje na vgrajenem tiskalniku ni uspelo. Preverite, da je v tiskalniku papir in da je pokrov zaprt.`,
      { cause: error },
    );
  }
}
